import { Sparkles, X, Loader2, Mic, MicOff, Send } from "lucide-react";

export function AIChatPanel({
  onClose,
  chatMessages,
  setChatMessages,
  chatInput,
  setChatInput,
  sendChatMessage,
  generateFromChat,
  assistantLoading,
  isRecording,
  startRecording,
  stopRecording,
  chatEndRef,
  pages,
}) {
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="assistant-panel">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white border-l border-gray-200 shadow-2xl h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-violet-600" />
            <h3 className="font-semibold text-sm text-gray-900">Assistant CardFlow</h3>
          </div>
          <div className="flex items-center gap-1">
            {chatMessages.filter(m => m.role === "user").length > 0 && (
              <button
                onClick={generateFromChat}
                disabled={assistantLoading}
                className="bg-violet-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1"
                data-testid="generate-schema-btn"
              >
                {assistantLoading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Générer
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
          {/* Welcome */}
          {chatMessages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-4">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center">
                <Sparkles size={28} className="text-violet-600" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-gray-900 mb-1">Décrivez votre projet</h4>
                <p className="text-xs text-gray-400 leading-relaxed max-w-xs">
                  Ecrivez ou dictez votre idee. Collez une liste de fonctionnalités, un tableau, ou expliquez simplement votre projet. L'IA genere le schéma automatiquement.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {["Une holding avec 3 filiales", "App SaaS avec landing, admin et client", "E-commerce avec catalogue et paiement"].map(ex => (
                  <button
                    key={ex}
                    onClick={() => { setChatMessages([{ role: "user", text: ex, type: "text" }]); }}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-[10px] text-gray-500 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-600 transition-colors"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {chatMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                msg.role === "user"
                  ? msg.type === "voice"
                    ? "bg-violet-600 text-white"
                    : "bg-indigo-600 text-white"
                  : msg.type === "error"
                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : msg.type === "loading"
                      ? "bg-gray-50 text-gray-500 border border-gray-200"
                      : "bg-emerald-50 text-emerald-800 border border-emerald-200"
              }`}>
                {msg.role === "user" && msg.type === "voice" && (
                  <div className="flex items-center gap-1 mb-1 opacity-70">
                    <Mic size={10} />
                    <span className="text-[9px] font-semibold uppercase">Vocal</span>
                  </div>
                )}
                {msg.type === "loading" && (
                  <div className="flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-violet-500" />
                    <span className="text-xs">{msg.text}</span>
                  </div>
                )}
                {msg.type !== "loading" && (
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                )}
                {msg.type === "result" && (
                  <div className="mt-2 flex items-center gap-1 opacity-60">
                    <Sparkles size={10} />
                    <span className="text-[9px] font-semibold">Schéma mis à jour</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Pages summary */}
        {pages.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50 shrink-0">
            <div className="flex items-center gap-2 overflow-x-auto">
              <span className="text-[9px] text-gray-400 font-semibold uppercase shrink-0">Pages:</span>
              <div className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" title="Vue principale" />
              {pages.map(p => (
                <span key={p.id} className="text-[10px] text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-200 whitespace-nowrap shrink-0">
                  {p.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-gray-200 px-4 py-3 shrink-0 bg-white">
          <div className="flex items-end gap-2">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={assistantLoading}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shrink-0 disabled:opacity-50 ${
                isRecording
                  ? "bg-rose-500 text-white animate-pulse"
                  : "bg-gray-100 text-gray-500 hover:bg-violet-100 hover:text-violet-600"
              }`}
              data-testid="record-btn"
              title={isRecording ? "Arreter" : "Dicter"}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </button>

            <div className="flex-1 relative">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                placeholder="Décrivez votre projet, collez une liste..."
                className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200 min-h-[42px] max-h-[120px]"
                rows={1}
                data-testid="chat-input"
              />
            </div>

            <button
              onClick={sendChatMessage}
              disabled={!chatInput.trim() || assistantLoading}
              className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 transition-colors shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="send-btn"
            >
              <Send size={16} />
            </button>
          </div>
          {isRecording && (
            <p className="text-[10px] text-rose-500 font-medium mt-1.5 text-center animate-pulse">
              Enregistrement en cours...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
